import React, { useEffect, useState } from 'react';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import OrganizationVariablesContainer from '../../../hooks/OrganizationVariablesContainer';
import OrganizationVariable from '../../../models/organizations/OrganizationVariable';
import Button from '../../../components/general/Button';
import SearchField from '../../../components/forms/SearchField';
import CustomScrollbars from '../../../components/structure/CustomScrollbars';
import { color_shades_dark, color_shades_darker, color_shades_darkest, color_colors_ocean } from '../../../constants/colors';
import OrganizationVariableEditModal from './OrganizationVariableEditModal';

const addIcon = require("../../../content/images/add-circle-dark.svg");

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    padding-bottom: 4px;
`;

const Title = styled.span`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${color_shades_darkest};
    line-height: 1;
`;

const AddButton = styled(Button)`
    height: 24px;
    width: 24px;
    min-height: 24px;
    min-width: 24px;
    margin-top: 4px;
    margin-bottom: 4px;
`;

interface OrganizationVariablesListProps {
    organizationId: string;
}

const OrganizationVariablesList: React.FC<OrganizationVariablesListProps> = (props) => {
    const variablesContainer = OrganizationVariablesContainer.useContainer();
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedVariable, setSelectedVariable] = useState<OrganizationVariable | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadVariables = async () => {
            if (props.organizationId) {
                setIsLoading(true);
                try {
                    await variablesContainer.loadOrganizationVariablesByOrganizationId(props.organizationId);
                } catch (error) {
                    console.error('Error loading organization variables:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        };
        loadVariables();
    }, [props.organizationId]);

    const handleCreateVariable = () => {
        setSelectedVariable({
            name: '',
            value: '',
            organizationId: props.organizationId
        });
        setIsEditModalOpen(true);
    };

    const handleEditVariable = (variable: OrganizationVariable) => {
        setSelectedVariable(variable);
        setIsEditModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsEditModalOpen(false);
        setSelectedVariable(null);
    };

    const handleSaveVariable = async (variable: OrganizationVariable) => {
        if (variable.id) {
            await variablesContainer.updateOrganizationVariable(variable.id, variable);
        } else {
            await variablesContainer.createOrganizationVariable(variable);
        }
        handleCloseModal();
    };

    const handleDeleteVariable = async (variable: OrganizationVariable) => {
        if (variable.id && window.confirm(`Are you sure you want to delete the constant "${variable.name}"?`)) {
            await variablesContainer.deleteOrganizationVariable(variable.id);
            handleCloseModal();
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
    };

    const filteredVariables = variablesContainer.organizationVariables?.filter(variable =>
        variable.name.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    if (!props.organizationId) {
        return (
            <div className={containerStyle}>
                <div className="empty-state">
                    <p>No organization selected.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={containerStyle}>
            <Header>
                <Title>Organization Constants</Title>
                <AddButton
                    themes={['icon']}
                    icon={addIcon}
                    onClick={handleCreateVariable}
                />
            </Header>

            <SearchField
                placeholder="Search constants by name..."
                value={searchTerm}
                onChange={handleSearchChange}
                handleClear={handleClearSearch}
                className="search-field-compact"
                boxType="square"
            />

            <div className="variables-list">
                <CustomScrollbars autoHide>
                    <div className="scrollable-content">
                        {isLoading ? (
                            <div className="empty-state">
                                <p>Loading constants...</p>
                            </div>
                        ) : filteredVariables.length === 0 ? (
                            <div className="empty-state">
                                {searchTerm ? (
                                    <p>No constants found matching "{searchTerm}". Try a different search term.</p>
                                ) : (
                                    <p>No constants found. Click the + button to create your first constant.</p>
                                )}
                            </div>
                        ) : (
                            filteredVariables.map((variable) => (
                                <div
                                    key={variable.id}
                                    className="variable-item"
                                    onClick={() => handleEditVariable(variable)}
                                >
                                    <div className="variable-name">{variable.name}</div>
                                    <div className="variable-value">{variable.value}</div>
                                </div>
                            ))
                        )}
                    </div>
                </CustomScrollbars>
            </div>

            {isEditModalOpen && selectedVariable && (
                <OrganizationVariableEditModal
                    variable={selectedVariable}
                    isVisible={isEditModalOpen}
                    onClose={handleCloseModal}
                    onSave={handleSaveVariable}
                    onDelete={handleDeleteVariable}
                />
            )}
        </div>
    );
};

const containerStyle = css`
    padding: 12px 16px 16px 16px;
    height: 100%;
    display: flex;
    flex-direction: column;

    .search-field-compact {
        margin-bottom: 8px !important;
        flex: none !important;

        > div:first-child {
            display: none !important;
        }

        > div:last-child > div {
            margin-top: 0 !important;
        }
    }

    .variables-list {
        flex: 1;
        overflow: hidden;
    }

    .scrollable-content {
        padding-bottom: 48px;
    }

    .empty-state {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100px;
        color: ${color_shades_darker};
        font-style: italic;
        text-align: center;
    }

    .variable-item {
        padding: 12px;
        border: 1px solid ${color_shades_dark};
        border-radius: 8px;
        margin-bottom: 8px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            border-color: ${color_colors_ocean};
            background-color: rgba(46, 125, 206, 0.05);
        }

        .variable-name {
            font-weight: 600;
            font-size: 14px;
            color: ${color_shades_darkest};
            margin-bottom: 4px;
        }

        .variable-value {
            font-size: 12px;
            color: ${color_shades_darker};
            word-break: break-all;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }
    }
`;

export default OrganizationVariablesList;
